/**
 * AdSegmentsWidget — T7 «Ad-viewer segments и ad × IAP»
 * ========================================================
 *
 * Спека: .claude/plans/2026-08-25-avz-ad-monetization-dashboards.md, T7.
 *
 * Три блока:
 *  - горизонтальный stacked bar: доля пользователей vs доля рекламной
 *    выручки по бакетам оплаченных показов (0 / 1–5 / 6–20 / 21–50 /
 *    51–150 / 151+);
 *  - бар payer rate по бакетам, последовательная одноцветная шкала;
 *  - воронка `ad_vip_action` (отмена/просмотр/покупка) + показы
 *    `ad_house_vip` (только здесь в разделе).
 *
 * Диапазон дат дашборда трактуется календарно (event_ts за последние
 * 30 полных дней по умолчанию), не как когорта регистрации.
 */

import React, { useMemo } from 'react';
import { BaseWidget, useBaseWidget } from '../BaseWidget';
import { useAdsWidgetData } from '../../../hooks/useAdsGeo';
import { AdsService } from '../../../services/adsService';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, Cell } from 'recharts';
import { Tooltip } from '../../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../../ui/responsive-chart';
import { adsLtv as adsLtvSpecs } from '@ggstat/chart-specs';

// Ряды, шкала и подписи — общие с MCP-рендером (shared/chart-specs/specs/adsLtv.json)
const SEGMENTS_SPEC = adsLtvSpecs.segments;
const [USERS_SERIES, REVENUE_SERIES] = SEGMENTS_SPEC.share;
const USERS_COLOR = USERS_SERIES.color;
const REVENUE_COLOR = REVENUE_SERIES.color;

/** Последовательная одноцветная шкала payer rate: светлее → темнее по убыванию/возрастанию значения. */
const PAYER_RATE_SCALE = SEGMENTS_SPEC.payerRateScale;
const BUCKET_LABEL = SEGMENTS_SPEC.bucketLabels;
const VIP_FUNNEL_SERIES = SEGMENTS_SPEC.vipFunnel[0];

const fmtInt = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtUsd = (v) => `$${Number(v || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;

/** users/adRevenue share -> строки для stacked-bar «доля пользователей vs доля выручки». */
function buildShareRows(buckets) {
  return (buckets || []).map((b) => ({
    bucket: BUCKET_LABEL[b.bucket] || b.bucket,
    usersShare: b.usersShare,
    adRevenueShare: b.adRevenueShare,
    users: b.users,
    adRevenue: b.adRevenue
  }));
}

function ShareTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium">Бакет: {label}</p>
      <p style={{ color: USERS_COLOR }}>Пользователи: {fmtPct(point.usersShare)} ({fmtInt(point.users)})</p>
      <p style={{ color: REVENUE_COLOR }}>Рекламная выручка: {fmtPct(point.adRevenueShare)} ({fmtUsd(point.adRevenue)})</p>
    </div>
  );
}

function PayerRateTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3 text-sm space-y-1">
      <p className="font-medium">Бакет: {label}</p>
      <p>Payer rate: {fmtPct(point.payerRate)}</p>
      <p className="text-muted-foreground">IAP ARPU: {fmtUsd(point.iapArpu)} · VIP-покупок: {fmtInt(point.vipBuyers)}</p>
    </div>
  );
}

export function AdSegmentsWidget() {
  const segmentsData = useAdsWidgetData(
    async (dashboardFilters, options) => AdsService.get('/segments', dashboardFilters, {}, options),
    []
  );

  const widgetProps = useBaseWidget(segmentsData);
  const { data } = widgetProps;

  const shareRows = useMemo(() => buildShareRows(data?.buckets), [data?.buckets]);
  const payerRateRows = useMemo(
    () => (data?.buckets || []).map((b) => ({
      bucket: BUCKET_LABEL[b.bucket] || b.bucket,
      payerRate: b.payerRate,
      iapArpu: b.iapArpu,
      vipBuyers: b.vipBuyers
    })),
    [data?.buckets]
  );

  const funnel = data?.vipFunnel;
  const houseAd = data?.houseAd;
  const meta = data?.meta;

  return (
    <BaseWidget
      {...widgetProps}
      title="Сегменты по рекламе и ad × IAP"
      subtitle="Кто смотрит рекламу, платит ли он и стоит ли позиционировать VIP как «без рекламы»"
      emptyMessage="Нет данных за выбранный диапазон"
      skeletonRows={10}
    >
      <div className="space-y-6">
        {/* Доля пользователей vs доля рекламной выручки по бакетам */}
        <div>
          <p className="text-sm font-medium mb-2">Доля пользователей vs доля рекламной выручки, по бакетам оплаченных показов</p>
          {shareRows.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(220, shareRows.length * 40)}>
              <BarChart data={shareRows} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)}%`} domain={[0, 1]} className="text-xs" />
                <YAxis type="category" dataKey="bucket" className="text-xs" width={70} />
                <Tooltip content={<ShareTooltip />} />
                <Legend />
                <Bar dataKey={USERS_SERIES.key} name={USERS_SERIES.label} fill={USERS_COLOR} isAnimationActive={false} />
                <Bar dataKey={REVENUE_SERIES.key} name={REVENUE_SERIES.label} fill={REVENUE_COLOR} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">Нет данных</div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Рекламная выручка здесь — событийная (admon, p2int=2), не биллинговая
            {meta ? `: ${fmtPct(meta.eventRevenueCoverage)} от prod_admon за окно` : ', ≈89% от биллинговой'}.
          </p>
        </div>

        {/* Payer rate по бакетам — последовательная одноцветная шкала */}
        <div>
          <p className="text-sm font-medium mb-2">Payer rate по бакетам</p>
          {payerRateRows.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(200, payerRateRows.length * 36)}>
              <BarChart data={payerRateRows} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)}%`} className="text-xs" />
                <YAxis type="category" dataKey="bucket" className="text-xs" width={70} />
                <Tooltip content={<PayerRateTooltip />} />
                <Bar dataKey={SEGMENTS_SPEC.payerRate.key} name={SEGMENTS_SPEC.payerRate.label} isAnimationActive={false}>
                  {payerRateRows.map((_, idx) => (
                    <Cell key={idx} fill={PAYER_RATE_SCALE[Math.min(idx, PAYER_RATE_SCALE.length - 1)]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">Нет данных</div>
          )}
        </div>

        {/* Воронка ad_vip_action */}
        <div>
          <p className="text-sm font-medium mb-2">Воронка ad_vip_action (просмотр/отмена/покупка VIP)</p>
          {funnel ? (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart
                data={SEGMENTS_SPEC.vipFunnelStages.map((st) => ({ stage: st.label, value: funnel[st.key] }))}
                layout="vertical"
                margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" className="text-xs" tickFormatter={fmtInt} />
                <YAxis type="category" dataKey="stage" className="text-xs" width={100} />
                <Tooltip formatter={(v) => fmtInt(v)} />
                <Bar dataKey={VIP_FUNNEL_SERIES.key} name={VIP_FUNNEL_SERIES.label} fill={VIP_FUNNEL_SERIES.color} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center py-6 text-sm text-muted-foreground">Нет данных</div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            Всего {fmtInt(funnel?.events)} событий, {fmtInt(funnel?.devices)} устройств. Описательная связка, без причинного вывода
            (VIP убирает рекламу — купившие могли решить это по другим причинам).
          </p>
        </div>

        {/* Показы ad_house_vip — только в этом разделе */}
        <div className="rounded-lg border p-3 bg-muted/30">
          <p className="text-sm font-medium">Показы ad_house_vip (домашняя реклама VIP)</p>
          <p className="text-sm text-muted-foreground">
            {fmtInt(houseAd?.impressions)} показов · {fmtInt(houseAd?.devices)} устройств.
            Это не сетевой показ (доход $0) — предложение купить VIP, а не монетизация рекламой;
            исключён из всех показов/eCPM метрик раздела и учитывается только здесь.
          </p>
        </div>
      </div>
    </BaseWidget>
  );
}

export default AdSegmentsWidget;
